import {
	AnimationEvent,
	Context,
	createContext,
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import classNames from "classnames";
import { Portal } from "../Portal/Portal";
import { BusyIndicator } from "../BusyIndicator/BusyIndicator";
import { ConditionalWrapper } from "../ConditionalWrapper/ConditionalWrapper";
import { setTrackingId } from "client/Misc/utils";
import { DialogBody } from "./DialogBody/DialogBody";
import { DialogCard } from "./DialogCard/DialogCard";
import { DialogHeader } from "./DialogHeader/DialogHeader";
import { DialogFooter } from "./DialogFooter/DialogFooter";
import styles from "./Dialog.module.less";
import { ContainsDialogsContext } from "client/Contexts/ContainsDialogsContext";
import {
	MediaQueryKind,
	useWorkspaceMediaQuery
} from "client/Hooks";

export type DialogSize =
	| "normal"
	| "long"
	| "large";

type CenteredDialogProps = {
	variant?: "centered";
	isEmbedded?: boolean;
	isEnlargeable?: boolean;
	size?: DialogSize;
	height?: "fill" | "auto";
};

type LeftSliderDialogProps = {
	variant: "left-slider";
	onClose: () => void;
};

export type DialogBackdropState = "visible" | "invisible";

export type DialogProps = {
	hasBusyIndicator?: boolean;
	backdrop?: DialogBackdropState;
	trackingId?: string;
} & (
	| CenteredDialogProps
	| LeftSliderDialogProps
);

type CommonDialogContextData = {
	isFullPageOnMobile: boolean;
	shouldAlwaysHaveSingleLineTitle: boolean;
	shouldBodyHaveVerticalPadding: boolean;
};

export type DialogContextData =
	& CommonDialogContextData
	& (
		{
			isEnlargeable: false;
			isEnlarged?: undefined;
			setIsEnlarged?: undefined;
		}
		|
		{
			isEnlargeable: true;
			isEnlarged: boolean;
			setIsEnlarged: (isEnlarged: boolean) => void;
		}
	);

const dialogSizeToClass: { [key in DialogSize]: string; } = {
	"large": styles.root_large,
	"long": styles.root_long,
	"normal": ""
};

export const DialogContext: Context<DialogContextData> = createContext<DialogContextData>({
	isEnlargeable: false,
	isFullPageOnMobile: false,
	shouldAlwaysHaveSingleLineTitle: false,
	shouldBodyHaveVerticalPadding: false
});

export const Dialog = (props: PropsWithChildren<DialogProps>): ReactElement => {
	const {
		variant = "centered",
		children,
		hasBusyIndicator = false,
		backdrop = "visible",
		trackingId
	} = props;

	const [isAnimating, setIsAnimating] = useState<boolean>(false);

	const onClose: (() => void) | undefined = props.variant === "left-slider"
		? props.onClose
		: undefined;

	const {
		isEmbedded = false,
		isEnlargeable,
		size = "normal",
		height = "auto"
	} = typeof props.variant === "undefined" || props.variant === "centered"
			? props
			: {
				isEmbedded: undefined,
				isEnlargeable: undefined,
				size: undefined,
				height: "fill"
			};

	const [isEnlarged, setIsEnlarged] = useState<boolean>(false);
	const backdropRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const dialogRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const {
		registerDialog
	} = useContext(ContainsDialogsContext);

	useEffect(
		(): EffectWithDestructorResult => {
			return registerDialog?.();
		},
		[registerDialog]
	);

	const dialogContextData = useMemo(
		(): DialogContextData => {
			const commonData: CommonDialogContextData = {
				isFullPageOnMobile: size === "large",
				shouldAlwaysHaveSingleLineTitle: size === "large",
				shouldBodyHaveVerticalPadding: size === "large" || variant === "left-slider"
			};

			if (!isEnlargeable) {
				return {
					...commonData,
					isEnlargeable: false,
				};
			}

			return {
				...commonData,
				isEnlargeable: true,
				isEnlarged,
				setIsEnlarged
			};
		},
		[
			isEnlargeable,
			isEnlarged,
			size,
			variant
		]
	);

	const isFullScreen: boolean = dialogContextData.isEnlarged
		|| (dialogContextData.isFullPageOnMobile && isMobile);

	const animationStartHandler = useCallback(
		(args: AnimationEvent): void => {
			if (args.target === dialogRef.current) {
				setIsAnimating(true);
			}
		},
		[]
	);

	const animationEndHandler = useCallback(
		(args: AnimationEvent): void => {
			if (args.target === dialogRef.current) {
				setIsAnimating(false);
			}
		},
		[]
	);

	const backdropClickHandler = useCallback(
		(args: MouseEvent): void => {
			args.stopPropagation();

			if (args.target === backdropRef.current
				&& !isAnimating
			) {
				onClose?.();
			}
		},
		[
			onClose,
			isAnimating
		]
	);

	return (
		<ConditionalWrapper
			condition={!isEmbedded}
			wrapper={children => <Portal container={document.body}>{children}</Portal>}
		>
			<DialogContext.Provider value={dialogContextData}>
				<div
					className={classNames(
						styles.container,
						{
							[styles.container_hasSafeAreaPaddings]: variant !== "left-slider"
								&& !isFullScreen,
							[styles.container_invisible]: backdrop === "invisible"
						}
					)}
					onClick={backdropClickHandler}
					ref={backdropRef}
				>
					<div
						{...setTrackingId(trackingId)}
						className={classNames(
							styles.root,
							dialogSizeToClass[size],
							{
								[styles.root_enlarged]: isEnlarged,
								[styles.root_fillHeight]: height === "fill",
								[styles.root_hasSafeAreaPaddings]: variant === "left-slider" || isFullScreen,
								[styles.root_leftSlider]: variant === "left-slider"
							}
						)}
						style={{ overflow: hasBusyIndicator ? "hidden" : undefined }}
						onAnimationStart={animationStartHandler}
						onAnimationEnd={animationEndHandler}
						ref={dialogRef}
					>
						{hasBusyIndicator && (
							<BusyIndicator />
						)}
						{children}
					</div>
				</div>
			</DialogContext.Provider>
		</ConditionalWrapper>
	);
};

Dialog.displayName = "Dialog";

export type DialogStepIndicatorProps = {
	amount: number;
	current: number;
};

const DialogStepIndicator = (props: DialogStepIndicatorProps): ReactElement => {
	const {
		amount,
		current
	} = props;

	return (
		<ol className={styles.stepIndicator}>
			{[...new Array(amount)].map((_, i) => (
				<li key={i} className={classNames({ [styles.active]: i === current })}/>
			))}
		</ol>
	);
};
DialogStepIndicator.displayName = "Dialog.StepIndicator";

Dialog.StepIndicator = DialogStepIndicator;
Dialog.Header = DialogHeader;
Dialog.Body = DialogBody;
Dialog.Footer = DialogFooter;
Dialog.Card = DialogCard;
